// hooks/useWithdrawBond.js

import { useState, useCallback } from 'react';
import { useConfig } from 'wagmi';
import { writeContract, waitForTransactionReceipt, estimateGas, getGasPrice, readContract } from 'wagmi/actions';
import { formatUnits } from 'viem';
import { newPool } from '../../../../utils/contract';

export const useWithdrawBond = () => {
    const [isWithdrawing, setIsWithdrawing] = useState(false);
    const [isEstimatingGas, setIsEstimatingGas] = useState(false);
    const [isLoadingBondAmount, setIsLoadingBondAmount] = useState(false);
    const config = useConfig();

    /**
     * Get bond amount from the contract
     */
    const getBondAmount = useCallback(
        async (contractAddress, decimals = 18) => {
            try {
                setIsLoadingBondAmount(true);
                const stakingContract = newPool;

                const result = await readContract(config, {
                    address: contractAddress,
                    abi: stakingContract.abi,
                    functionName: 'bondAmount',
                    args: [],
                });

                const bondValue = Array.isArray(result) ? result[0] : result;
                const bondETH = formatUnits(bondValue || 0n, decimals);
                const bondFormatted = parseFloat(bondETH).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 6
                });

                return {
                    bondAmountWei: bondValue?.toString() || "0",
                    bondAmountETH: bondETH,
                    bondAmountFormatted: bondFormatted,
                    bondAmountNumber: parseFloat(bondETH)
                };
            } catch (error) {
                console.error("Error getting bond amount:", error);
                throw error;
            } finally {
                setIsLoadingBondAmount(false);
            }
        },
        [config]
    );

    /**
     * Withdraw bond amount from the pool
     * No arguments needed - contract handles the bond amount internally
     */
    const withdrawBondAmount = useCallback(
        async (contractAddress) => {
            try {
                setIsWithdrawing(true);
                const stakingContract = newPool;

                const hash = await writeContract(config, {
                    address: contractAddress,
                    abi: stakingContract.abi,
                    functionName: 'withdrawBondAmount',
                    args: [],
                });

                const receipt = await waitForTransactionReceipt(config, { hash });
                return { receipt, hash };
            } catch (error) {
                console.error("Error withdrawing bond amount:", error);
                throw error;
            } finally {
                setIsWithdrawing(false);
            }
        },
        [config]
    );

    /**
     * Estimate gas for withdrawBondAmount transaction
     */
    const estimateWithdrawBondGas = useCallback(
        async (contractAddress) => {
            try {
                setIsEstimatingGas(true);
                const stakingContract = newPool;

                const estimatedGas = await estimateGas(config, {
                    address: contractAddress,
                    abi: stakingContract.abi,
                    functionName: 'withdrawBondAmount',
                    args: [],
                });

                const gasPrice = await getGasPrice(config);
                const totalGasCost = estimatedGas * gasPrice;
                const totalGasCostETH = formatUnits(totalGasCost, 18);

                return {
                    estimatedGas: estimatedGas?.toString(),
                    gasPrice: gasPrice?.toString(),
                    totalGasCostWei: totalGasCost?.toString(),
                    totalGasCostETH: parseFloat(totalGasCostETH),
                    totalGasCostETHFormatted: totalGasCostETH
                };
            } catch (error) {
                console.error("Error estimating gas for withdrawBondAmount:", error);
                throw error;
            } finally {
                setIsEstimatingGas(false);
            }
        },
        [config]
    );

    return {
        // States
        isWithdrawing,
        isEstimatingGas,
        isLoadingBondAmount,

        // Actions
        withdrawBondAmount,
        estimateWithdrawBondGas,
        getBondAmount,
    };
};